import React, { useEffect, useState } from 'react';
import { Link2, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi, toastError, useBusy } from './adminApi';
import { Field } from './AdminUi';

/*
 * Campaign create / edit / import dialogs. Each one owns its form state,
 * submits once (in-flight state, no double submit) and reports the new or
 * edited campaign id through onDone so the list can select it.
 */

function SubmitLabel({ busy, idle, pending, icon: Icon }) {
  if (busy) return <><Loader2 className="is-spinner" aria-hidden="true" />{pending}</>;
  return <>{Icon && <Icon aria-hidden="true" />}{idle}</>;
}

export function CreateCampaignDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const { run, busy } = useBusy();
  useEffect(() => { if (open) setForm({ name: '', description: '' }); }, [open]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    run('create', async () => {
      try {
        const res = await adminApi.post('/admin/threat-hunt-campaigns', { name: form.name.trim(), description: form.description });
        toast.success('Draft campaign created', { description: 'It stays hidden from customers until you publish it.' });
        onOpenChange(false);
        onDone?.(res.data?.campaign?.id);
      } catch (err) {
        toastError(err, 'Could not create the campaign');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy('create') && onOpenChange(v)}>
      <DialogContent className="adm-dialog">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>New hunt campaign</DialogTitle>
            <DialogDescription>Campaigns start as drafts. Add IOCs, then publish to make them visible to customers.</DialogDescription>
          </DialogHeader>
          <Field label="Campaign name">
            {(id) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. APT28 credential phishing, Oct 2026" autoFocus />}
          </Field>
          <Field label="Description" hint="Markdown is supported.">
            {(id, hint) => <Textarea id={id} aria-describedby={hint} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />}
          </Field>
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy('create')}>Cancel</Button>
            <Button type="submit" disabled={busy('create')}>
              <SubmitLabel busy={busy('create')} idle="Create draft" pending="Creating…" />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditCampaignDialog({ campaign, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const { run, busy } = useBusy();
  useEffect(() => {
    if (campaign) setForm({ name: campaign.name || '', description: campaign.description || '' });
  }, [campaign]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    run('save', async () => {
      try {
        await adminApi.put(`/admin/threat-hunt-campaigns/${campaign.id}`, { name: form.name.trim(), description: form.description });
        toast.success('Campaign updated');
        onOpenChange(false);
        onDone?.(campaign.id);
      } catch (err) {
        toastError(err, 'Could not save the campaign');
      }
    });
  };

  return (
    <Dialog open={Boolean(campaign)} onOpenChange={(v) => !busy('save') && onOpenChange(v)}>
      <DialogContent className="adm-dialog">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>Edit campaign</DialogTitle>
            <DialogDescription>
              {campaign?.status === 'approved' ? 'This campaign is live: customers see changes immediately.' : 'Changes are saved to the draft.'}
            </DialogDescription>
          </DialogHeader>
          <Field label="Campaign name">
            {(id) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          </Field>
          <Field label="Description" hint="Markdown is supported.">
            {(id, hint) => <Textarea id={id} aria-describedby={hint} rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />}
          </Field>
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy('save')}>Cancel</Button>
            <Button type="submit" disabled={busy('save')}>
              <SubmitLabel busy={busy('save')} idle="Save changes" pending="Saving…" />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function OtxImportDialog({ open, onOpenChange, onDone }) {
  const [url, setUrl] = useState('');
  const { run, busy } = useBusy();
  useEffect(() => { if (open) setUrl(''); }, [open]);

  const submit = (e) => {
    e.preventDefault();
    if (!/^https?:\/\/\S+$/i.test(url.trim())) {
      toast.error('Enter the full OTX pulse URL', { description: 'For example https://otx.alienvault.com/pulse/6789abcdef123456' });
      return;
    }
    run('import', async () => {
      try {
        const res = await adminApi.post('/admin/import-otx-pulse', { pulse_url: url.trim() });
        toast.success(res.data?.message || 'Pulse imported as a draft campaign');
        onOpenChange(false);
        onDone?.(res.data?.campaign?.id);
      } catch (err) {
        toastError(err, 'Could not import the OTX pulse');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy('import') && onOpenChange(v)}>
      <DialogContent className="adm-dialog">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>Import an OTX pulse</DialogTitle>
            <DialogDescription>The pulse's indicators become a draft campaign for your review. Nothing is published.</DialogDescription>
          </DialogHeader>
          <Field label="OTX pulse URL" hint="Example: https://otx.alienvault.com/pulse/6789abcdef123456">
            {(id, hint) => (
              <Input id={id} aria-describedby={hint} type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://otx.alienvault.com/pulse/…" className="adm-mono" autoFocus />
            )}
          </Field>
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy('import')}>Cancel</Button>
            <Button type="submit" disabled={busy('import')}>
              <SubmitLabel busy={busy('import')} idle="Import pulse" pending="Importing…" icon={Link2} />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CsvImportDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: '', description: '', content: '', fileName: '' });
  const { run, busy } = useBusy();
  useEffect(() => { if (open) setForm({ name: '', description: '', content: '', fileName: '' }); }, [open]);

  const readFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, content: String(ev.target.result || ''), fileName: file.name }));
    reader.onerror = () => toast.error(`Could not read ${file.name}`);
    reader.readAsText(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error('Upload a CSV file or paste its content');
      return;
    }
    run('import', async () => {
      try {
        const res = await adminApi.post('/admin/import-csv-iocs', {
          csv_content: form.content,
          campaign_name: form.name,
          campaign_description: form.description,
        });
        toast.success(res.data?.message || 'CSV imported as a draft campaign');
        onOpenChange(false);
        onDone?.(res.data?.campaign?.id);
      } catch (err) {
        toastError(err, 'Could not import the CSV');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy('import') && onOpenChange(v)}>
      <DialogContent className="adm-dialog adm-dialog--wide">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>Import IOCs from CSV</DialogTitle>
            <DialogDescription>The rows become a draft campaign for your review. Nothing is published.</DialogDescription>
          </DialogHeader>
          <div className="adm-form-row">
            <Field label="Campaign name (optional)">
              {(id) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Generated from the file if empty" />}
            </Field>
            <Field label="Description (optional)">
              {(id) => <Input id={id} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />}
            </Field>
          </div>
          <Field label="CSV file">
            {(id) => (
              <div className="adm-file">
                <input id={id} type="file" accept=".csv,.txt,text/csv,text/plain" onChange={readFile} className="adm-file-input" />
                <label htmlFor={id} className="is-btn is-btn--secondary is-btn--sm">
                  <Upload aria-hidden="true" /> Choose file
                </label>
                <span className="adm-meta">{form.fileName || 'No file chosen'}</span>
              </div>
            )}
          </Field>
          <Field label="CSV content" hint="Columns: type,value,description,source. A single value per line also works; the type is detected.">
            {(id, hint) => (
              <Textarea
                id={id}
                aria-describedby={hint}
                rows={8}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="adm-mono"
                placeholder={'type,value,description,source\nip,203.0.113.10,C2 server,Vendor report\ndomain,evil.example,Phishing,'}
              />
            )}
          </Field>
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy('import')}>Cancel</Button>
            <Button type="submit" disabled={busy('import')}>
              <SubmitLabel busy={busy('import')} idle="Import CSV" pending="Importing…" icon={Upload} />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
